import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import {
  createCrmActivity,
  createCrmContact,
  getCrmJobSummary,
  listCrmActivities,
  listCrmContacts,
  listCrmJobs,
  patchCrmContact,
  patchCrmActivity,
} from "../../api/crm";
import { listJobs } from "../../api/jobs";

const TABS = ["overview", "pipeline", "contacts", "activities"];
const PIPELINE_STAGES = ["lead", "contacted", "scheduled", "closed"];

export default function CrmWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = React.useState("overview");
  const [jobs, setJobs] = React.useState([]);
  const [jobId, setJobId] = React.useState("");
  const [summary, setSummary] = React.useState(null);
  const [contacts, setContacts] = React.useState([]);
  const [activities, setActivities] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [contactForm, setContactForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    status: "lead",
    notes: "",
  });
  const [activityForm, setActivityForm] = React.useState({
    contact_id: "",
    kind: "follow_up",
    summary: "",
    due_at: "",
  });
  const [dragContactId, setDragContactId] = React.useState("");
  const [dragOverStage, setDragOverStage] = React.useState("");

  const loadAll = React.useCallback(async (selectedJobId = "") => {
    setLoading(true);
    setError("");
    try {
      const [crmJobs, rawJobs] = await Promise.all([listCrmJobs(), listJobs({ limit: 200, offset: 0 })]);
      const mergedJobs = Array.isArray(rawJobs) ? rawJobs : [];
      const candidateJobId =
        selectedJobId ||
        searchParams.get("jobId") ||
        (mergedJobs[0] && String(mergedJobs[0].id)) ||
        "";
      setJobs(mergedJobs);
      setJobId(candidateJobId);
      if (candidateJobId) {
        const [sum, c, a] = await Promise.all([
          getCrmJobSummary(candidateJobId),
          listCrmContacts(candidateJobId),
          listCrmActivities(candidateJobId),
        ]);
        setSummary(sum);
        setContacts(c);
        setActivities(a);
      } else {
        setSummary(null);
        setContacts([]);
        setActivities([]);
      }
      void crmJobs; // kept for future board enhancements
    } catch (e) {
      const status = Number(e?.status || 0);
      if (status === 401) {
        setError("Your session expired. Please sign in again.");
        navigate(`/login?next=${encodeURIComponent(`/app/crm${window.location.search || ""}`)}`, { replace: true });
        return;
      }
      setError(e?.message || "Failed to load CRM data");
    } finally {
      setLoading(false);
    }
  }, [navigate, searchParams]);

  React.useEffect(() => {
    const incoming = (searchParams.get("tab") || "").toLowerCase();
    if (TABS.includes(incoming)) {
      setTab(incoming);
    }
  }, [searchParams]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleJobChange = async (nextJobId) => {
    const id = String(nextJobId || "");
    const next = new URLSearchParams(searchParams);
    if (id) next.set("jobId", id);
    else next.delete("jobId");
    setSearchParams(next, { replace: true });
    await loadAll(id);
  };

  const handleTab = (nextTab) => {
    const t = String(nextTab || "overview").toLowerCase();
    const finalTab = TABS.includes(t) ? t : "overview";
    setTab(finalTab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", finalTab);
    setSearchParams(next, { replace: true });
  };

  const submitContact = async () => {
    if (!jobId) return;
    if (!String(contactForm.name || "").trim()) {
      setError("Contact name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createCrmContact(jobId, contactForm);
      setContactForm({ name: "", email: "", phone: "", company: "", status: "lead", notes: "" });
      await loadAll(jobId);
      handleTab("contacts");
    } catch (e) {
      setError(e?.message || "Failed to save contact");
    } finally {
      setSaving(false);
    }
  };

  const submitActivity = async () => {
    if (!jobId) return;
    if (!String(activityForm.summary || "").trim()) {
      setError("Activity summary is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createCrmActivity(jobId, activityForm);
      setActivityForm({ contact_id: "", kind: "follow_up", summary: "", due_at: "" });
      await loadAll(jobId);
      handleTab("activities");
    } catch (e) {
      setError(e?.message || "Failed to save activity");
    } finally {
      setSaving(false);
    }
  };

  const toggleActivity = async (activity) => {
    setSaving(true);
    setError("");
    try {
      await patchCrmActivity(activity.id, { completed: !Boolean(activity.completed) });
      await loadAll(jobId);
    } catch (e) {
      setError(e?.message || "Failed to update activity");
    } finally {
      setSaving(false);
    }
  };

  const moveContactStage = async (contact, nextStatus) => {
    if (!contact?.id) return;
    const status = String(nextStatus || "").trim().toLowerCase();
    if (!PIPELINE_STAGES.includes(status)) return;
    setSaving(true);
    setError("");
    try {
      await patchCrmContact(contact.id, { status });
      await loadAll(jobId);
      handleTab("pipeline");
    } catch (e) {
      setError(e?.message || "Failed to update contact stage");
    } finally {
      setSaving(false);
    }
  };

  const onDragStartContact = (event, contact) => {
    const id = String(contact?.id || "");
    if (!id) return;
    setDragContactId(id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", id);
  };

  const onDragEndContact = () => {
    setDragContactId("");
    setDragOverStage("");
  };

  const onDragOverStage = (event, stage) => {
    event.preventDefault();
    if (!PIPELINE_STAGES.includes(stage)) return;
    event.dataTransfer.dropEffect = "move";
    setDragOverStage(stage);
  };

  const onDropToStage = async (event, stage) => {
    event.preventDefault();
    const targetStage = String(stage || "").toLowerCase();
    setDragOverStage("");
    if (!PIPELINE_STAGES.includes(targetStage)) return;
    const idFromEvent = String(event.dataTransfer.getData("text/plain") || "");
    const contactId = idFromEvent || dragContactId;
    if (!contactId) return;
    const contact = contacts.find((c) => String(c?.id || "") === contactId);
    if (!contact) return;
    const current = String(contact?.status || "lead").toLowerCase();
    if (current === targetStage) return;
    await moveContactStage(contact, targetStage);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">CRM Workspace</h1>
          <p className="mt-1 text-sm text-slate-500">Track client contacts, follow-ups, and job activity in one place.</p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
            <select
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
              value={jobId}
              onChange={(e) => handleJobChange(e.target.value)}
            >
              <option value="">Select a job</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name || "(untitled)"} - {j.id}
                </option>
              ))}
            </select>
            <Link to="/app/jobs">
              <Button variant="secondary" className="w-full">Jobs</Button>
            </Link>
            <Link to="/app/build">
              <Button variant="ghost" className="w-full">Build</Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant={tab === "overview" ? "primary" : "secondary"} onClick={() => handleTab("overview")}>Overview</Button>
            <Button variant={tab === "pipeline" ? "primary" : "secondary"} onClick={() => handleTab("pipeline")}>Pipeline</Button>
            <Button variant={tab === "contacts" ? "primary" : "secondary"} onClick={() => handleTab("contacts")}>Contacts</Button>
            <Button variant={tab === "activities" ? "primary" : "secondary"} onClick={() => handleTab("activities")}>Activities</Button>
          </div>
        </CardBody>
      </Card>

      {loading ? <p className="text-sm text-slate-500">Loading CRM...</p> : null}

      {!loading && tab === "overview" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Overview</h2></CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Contacts</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{summary?.contacts ?? 0}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Activities</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{summary?.activities ?? 0}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Open Tasks</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{summary?.open_activities ?? 0}</p>
            </div>
            <div className="sm:col-span-3">
              <h3 className="text-sm font-semibold text-slate-800">Next Activities</h3>
              <ul className="mt-2 space-y-2">
                {(summary?.next_activities || []).length < 1 ? (
                  <li className="text-sm text-slate-500">No upcoming activities.</li>
                ) : (
                  summary.next_activities.map((a) => (
                    <li key={a.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                      {a.summary} {a.due_at ? <span className="text-slate-500">(due {a.due_at})</span> : null}
                    </li>
                  ))
                )}
              </ul>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "pipeline" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Pipeline</h2></CardHeader>
          <CardBody>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {PIPELINE_STAGES.map((stage) => {
                const stageItems = contacts.filter((c) => String(c?.status || "lead").toLowerCase() === stage);
                return (
                  <div
                    key={stage}
                    className={`rounded-xl border p-3 transition ${
                      dragOverStage === stage ? "border-red-400 bg-red-50/70" : "border-slate-200 bg-slate-50"
                    }`}
                    onDragOver={(e) => onDragOverStage(e, stage)}
                    onDrop={(e) => onDropToStage(e, stage)}
                    onDragLeave={() => setDragOverStage((prev) => (prev === stage ? "" : prev))}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-semibold capitalize text-slate-900">{stage}</h3>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-600">{stageItems.length}</span>
                    </div>
                    <div className="space-y-2">
                      {stageItems.length < 1 ? (
                        <p className="text-xs text-slate-500">No contacts</p>
                      ) : (
                        stageItems.map((c) => {
                          const idx = PIPELINE_STAGES.indexOf(stage);
                          const canBack = idx > 0;
                          const canNext = idx < PIPELINE_STAGES.length - 1;
                          return (
                            <div
                              key={c.id}
                              className={`rounded-lg border bg-white p-2 ${
                                dragContactId === c.id ? "border-red-300 shadow-sm" : "border-slate-200"
                              }`}
                              draggable
                              onDragStart={(e) => onDragStartContact(e, c)}
                              onDragEnd={onDragEndContact}
                            >
                              <p className="text-sm font-medium text-slate-900">{c.name}</p>
                              <p className="text-xs text-slate-500">{c.company || c.email || c.phone || "No details yet"}</p>
                              <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">Drag to move stage</p>
                              <div className="mt-2 flex gap-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={!canBack || saving}
                                  onClick={() => moveContactStage(c, PIPELINE_STAGES[idx - 1])}
                                >
                                  Back
                                </Button>
                                <Button
                                  size="sm"
                                  disabled={!canNext || saving}
                                  onClick={() => moveContactStage(c, PIPELINE_STAGES[idx + 1])}
                                >
                                  Next
                                </Button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "contacts" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Contacts</h2></CardHeader>
          <CardBody className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder="Name" value={contactForm.name} onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))} />
              <Input placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} />
              <Input placeholder="Phone" value={contactForm.phone} onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))} />
              <Input placeholder="Company" value={contactForm.company} onChange={(e) => setContactForm((p) => ({ ...p, company: e.target.value }))} />
              <Input className="sm:col-span-2" placeholder="Notes" value={contactForm.notes} onChange={(e) => setContactForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            <Button onClick={submitContact} disabled={!jobId || saving}>{saving ? "Saving..." : "Add Contact"}</Button>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-slate-600">Name</th>
                    <th className="px-3 py-2 text-left text-slate-600">Email</th>
                    <th className="px-3 py-2 text-left text-slate-600">Phone</th>
                    <th className="px-3 py-2 text-left text-slate-600">Company</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length < 1 ? (
                    <tr><td colSpan={4} className="px-3 py-3 text-slate-500">No contacts yet.</td></tr>
                  ) : (
                    contacts.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-900">{c.name}</td>
                        <td className="px-3 py-2 text-slate-700">{c.email || ""}</td>
                        <td className="px-3 py-2 text-slate-700">{c.phone || ""}</td>
                        <td className="px-3 py-2 text-slate-700">{c.company || ""}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && tab === "activities" ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold text-slate-900">Activities</h2></CardHeader>
          <CardBody className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <select
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
                value={activityForm.contact_id}
                onChange={(e) => setActivityForm((p) => ({ ...p, contact_id: e.target.value }))}
              >
                <option value="">No contact (job-level)</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <Input placeholder="Kind (follow_up, note, call)" value={activityForm.kind} onChange={(e) => setActivityForm((p) => ({ ...p, kind: e.target.value }))} />
              <Input className="sm:col-span-2" placeholder="Summary" value={activityForm.summary} onChange={(e) => setActivityForm((p) => ({ ...p, summary: e.target.value }))} />
              <Input type="datetime-local" value={activityForm.due_at} onChange={(e) => setActivityForm((p) => ({ ...p, due_at: e.target.value }))} />
            </div>
            <Button onClick={submitActivity} disabled={!jobId || saving}>{saving ? "Saving..." : "Add Activity"}</Button>
            <ul className="space-y-2">
              {activities.length < 1 ? (
                <li className="text-sm text-slate-500">No activities yet.</li>
              ) : (
                activities.map((a) => (
                  <li key={a.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-medium text-slate-900">{a.summary}</p>
                        <p className="text-slate-500">{a.kind}{a.due_at ? ` - due ${a.due_at}` : ""}</p>
                      </div>
                      <Button size="sm" variant={a.completed ? "secondary" : "primary"} onClick={() => toggleActivity(a)} disabled={saving}>
                        {a.completed ? "Reopen" : "Mark Done"}
                      </Button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
